import dynamic from 'next/dynamic';
import { HeaderVariant, LayoutProps } from 'types/global';

import Button from 'molecules/Button';
import Image from 'molecules/Image';
import Link from 'molecules/Link';

import type { FC } from 'react';

const Header = dynamic(() => import('layout/header'));
const ConnectNav = dynamic(() => import('components/ConnectNav'));

type HeaderLandingProps = {
  variant?: HeaderVariant;
};

const HeaderLandingStatic: FC<HeaderLandingProps> = ({ variant }) => {
  if (variant === 'centeredLogoNoDemo') {
    return (
      <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-16 lg:px-8">
        <div className="relative flex h-18 w-full items-center justify-center lg:h-20 lg:py-5">
          <Link href="/" enablePreview={false} className="block w-[140px]">
            <Image
              url="/prismatic-logo.svg"
              width={140}
              height={29}
              alt="Prismatic logo"
              noFill
              priority
              className="h-auto w-full object-contain"
            />
          </Link>
        </div>
      </div>
    );
  }

  if (variant === 'seeADemo') {
    return (
      <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-16 lg:px-8">
        <div className="relative flex h-18 w-full items-center justify-between gap-6 lg:h-20 lg:py-5">
          <div className="w-[140px]">
            <Link href="/" enablePreview={false}>
              <Image
                url="/prismatic-logo.svg"
                width={140}
                height={29}
                alt="Prismatic logo"
                noFill
                priority
                className="h-auto w-full object-contain"
              />
            </Link>
          </div>
          <Button href="#demo" hierarchy="secondary" key="see-a-demo" label="See a demo" />
        </div>
      </div>
    );
  }

  if (variant === 'logoFreeTrial') {
    return (
      <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-16 lg:px-8">
        <div className="relative flex h-18 w-full items-center justify-between gap-6 lg:h-20 lg:py-5">
          <div className="w-[140px]">
            <Link href="/" enablePreview={false}>
              <Image
                url="/prismatic-logo.svg"
                width={140}
                height={29}
                alt="Prismatic logo"
                noFill
                className="h-auto w-full object-contain"
              />
            </Link>
          </div>
          <Button href="/free-trial/" hierarchy="secondary" key="start-free-trial" label="Start a free trial" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-xl px-4 sm:px-16 lg:px-8">
      <div className="relative flex h-18 w-full items-center justify-between gap-6 lg:h-20 lg:py-5">
        <div className="w-[140px]">
          <Link href="/" enablePreview={false}>
            <Image
              url="/prismatic-logo.svg"
              width={140}
              height={29}
              alt="Prismatic logo"
              noFill
              priority
              className="h-auto w-full object-contain"
            />
          </Link>
        </div>
        <Button href="/request-a-demo/" hierarchy="primary" key="get-a-demo" className="w-full" label="Get a Demo" />
      </div>
    </div>
  );
};

export const HeaderLandingWithBuilder = ({ content, preview, variant }: LayoutProps & { variant?: HeaderVariant }) => {
  if (variant === 'hidden') {
    return null;
  }

  if (variant === 'connect') {
    return <ConnectNav />;
  }

  const v: HeaderVariant =
    variant === 'centeredLogoNoDemo' ||
    variant === 'logoFreeTrial' ||
    variant === 'seeADemo' ||
    variant === 'standardHeader'
      ? variant
      : 'default';

  const key = `hdr-${v}-${preview ? 'preview' : 'prod'}`;

  if (v === 'standardHeader') {
    return (
      <header key={key} className="dark sticky top-0 z-[999] h-18 bg-gray-900 lg:h-20">
        <Header {...content?.data} />
      </header>
    );
  }

  return (
    <header key={key} className="dark sticky top-0 z-[999] bg-gray-900">
      <HeaderLandingStatic variant={v} />
    </header>
  );
};

export default HeaderLandingWithBuilder;
